import {useEffect, useRef} from 'react';
import {Conversation} from '@stripe-internal/conversations-client';
import {defineMessages} from 'react-intl';
import {isDotComConversation, isLlmConversation} from './conversationUtils';

export const LLM_DISCLAIMER_ID = 'llm-disclaimer';

export const messages = defineMessages({
  llmDisclaimer: {
    id: 'support_conversations_client.chatScreen.llmDisclaimer',
    description:
      'Legal disclaimer shown at the top of LLM conversations to inform users they are chatting with an AI',
    defaultMessage:
      'By messaging, you understand how <aiLink>Stripe uses AI</aiLink>{br}and acknowledge our <privacyLink>Privacy Policy</privacyLink>.',
  },
});

type Props = {
  conversation: Conversation;
};

export function useLlmDisclaimer({conversation}: Props) {
  const hasShownDisclaimer = useRef(false);

  useEffect(() => {
    // Only show disclaimer for conversations on stripe.com
    if (!isDotComConversation(conversation)) {
      return;
    }

    // Only show disclaimer once per conversation
    if (hasShownDisclaimer.current) {
      return;
    }

    // Only show for LLM conversations
    if (!isLlmConversation(conversation)) {
      return;
    }

    const triggerPoint = conversation.context.triggerParams?.triggerPoint;
    const isSelfServeRedirect =
      triggerPoint === 'dot_com_embedded_csf_self_serve';

    // Only show disclaimer on self-serve redirect
    if (!isSelfServeRedirect) {
      return;
    }

    // Check if disclaimer was already added (e.g., conversation resumed)
    const existingItems = conversation.log.get();
    const disclaimerExists = existingItems.some(
      (item: any) => item.id === LLM_DISCLAIMER_ID,
    );

    if (disclaimerExists) {
      hasShownDisclaimer.current = true;
      return;
    }

    // Prepend the disclaimer to the beginning of the log
    conversation.log.set([
      {
        id: LLM_DISCLAIMER_ID,
        name: 'conversation-meta',
        message: messages.llmDisclaimer,
        timestamp: 0,
      },
      ...existingItems,
    ]);

    hasShownDisclaimer.current = true;
  }, [conversation]);
}
